import type { ReactNode } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Chip, IconButton, Text, Tooltip, color, config, toRem } from 'folds';
import { TooltipProvider } from '$components/overlay-stack';
import {
  Check,
  EyeSlash,
  File,
  Image,
  Info,
  PencilSimple,
  VideoCamera,
  X,
  sizedIcon,
  type PhosphorIcon,
  phosphorSizeRem,
} from '$components/icons/phosphor';
import type { HTMLReactParserOptions } from 'html-react-parser';
import { Play, Pause } from '@phosphor-icons/react';
import { useMediaAuthentication } from '$hooks/useMediaAuthentication';
import { Image as MediaImage } from '$components/media';
import type { Opts as LinkifyOpts } from 'linkifyjs';
import { getReactCustomHtmlParser, LINKIFY_OPTS } from '$plugins/react-custom-html-parser';
import { useSpoilerClickHandler } from '$hooks/useSpoilerClickHandler';
import { RenderBody } from '$components/message';
import type { UploadSuccess } from '$state/upload';
import { UploadStatus, useBindUploadAtom } from '$state/upload';
import { useMatrixClient } from '$hooks/useMatrixClient';
import type { TUploadContent } from '$utils/matrix';
import { isImageMimeType } from '$utils/mimeTypes';
import { bytesToSize } from '$utils/common';
import type { TUploadItem, TUploadMetadata } from '$state/room/roomInputDrafts';
import { roomUploadAtomFamily } from '$state/room/roomInputDrafts';
import { useObjectURL } from '$hooks/useObjectURL';
import { useMediaConfig } from '$hooks/useMediaConfig';
import { useSettingsLinkBaseUrl } from '$features/settings/useSettingsLinkBaseUrl';
import { useSetting } from '$state/hooks/settings';
import { settingsAtom } from '$state/settings';
import { UploadCard, UploadCardError, UploadCardProgress } from './UploadCard';
import * as css from './UploadCard.css';
import { DescriptionEditor } from './UploadDescriptionEditor';

function getFileTypeIconComponent(fileType: string): PhosphorIcon {
  const type = fileType.toLowerCase();
  if (type.startsWith('audio')) return Play;
  if (type.startsWith('video')) return VideoCamera;
  if (isImageMimeType(type)) return Image;
  return File;
}

type PreviewImageProps = {
  fileItem: TUploadItem;
};
function PreviewImage({ fileItem }: Readonly<PreviewImageProps>) {
  const { originalFile, metadata } = fileItem;
  const fileUrl = useObjectURL(originalFile);

  return (
    <MediaImage
      style={{
        objectFit: 'contain',
        width: '100%',
        height: toRem(128),
        filter: metadata.markedAsSpoiler ? 'blur(44px)' : undefined,
      }}
      alt={originalFile.name}
      src={fileUrl}
    />
  );
}

type PreviewVideoProps = {
  fileItem: TUploadItem;
};
function PreviewVideo({ fileItem }: Readonly<PreviewVideoProps>) {
  const { originalFile, metadata } = fileItem;
  const fileUrl = useObjectURL(originalFile);

  return (
    // oxlint-disable-next-line jsx-a11y/media-has-caption
    <video
      style={{
        objectFit: 'contain',
        width: '100%',
        height: toRem(128),
        filter: metadata.markedAsSpoiler ? 'blur(44px)' : undefined,
      }}
      src={fileUrl}
    />
  );
}

const BAR_COUNT = 44;

function formatAudioTime(s: number): string {
  if (!Number.isFinite(s) || s < 0) return '0:00';
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, '0')}`;
}

type PreviewAudioProps = {
  fileItem: TUploadItem;
};
function PreviewAudio({ fileItem }: PreviewAudioProps) {
  const { originalFile, metadata } = fileItem;
  const audioUrl = useObjectURL(originalFile);
  const { waveform, audioDuration } = metadata;
  const duration = audioDuration ?? 0;

  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const rafRef = useRef<number | null>(null);

  const bars = useMemo(() => {
    if (!waveform || waveform.length === 0) {
      return Array(BAR_COUNT).fill(0.3);
    }
    if (waveform.length <= BAR_COUNT) {
      const step = (waveform.length - 1) / (BAR_COUNT - 1);
      return Array.from({ length: BAR_COUNT }, (_, i) => {
        const position = i * step;
        const lower = Math.floor(position);
        const upper = Math.min(Math.ceil(position), waveform.length - 1);
        const fraction = position - lower;
        if (lower === upper) {
          return waveform[lower] ?? 0.3;
        }
        return (waveform[lower] ?? 0.3) * (1 - fraction) + (waveform[upper] ?? 0.3) * fraction;
      });
    }
    const step = waveform.length / BAR_COUNT;
    return Array.from({ length: BAR_COUNT }, (_, i) => {
      const start = Math.floor(i * step);
      const end = Math.floor((i + 1) * step);
      const slice = waveform.slice(start, end);
      return slice.length > 0 ? Math.max(...slice) : 0.3;
    });
  }, [waveform]);

  const progress = duration > 0 ? Math.min(currentTime / duration, 1) : 0;
  const previewBars = useMemo(
    () =>
      bars.map((level, index) => ({
        id: `upload-audio-bar-${index}`,
        level,
        ratio: index / BAR_COUNT,
      })),
    [bars]
  );

  useEffect(() => {
    if (!audioUrl) {
      return undefined;
    }
    const audio = new Audio(audioUrl);
    audio.preload = 'auto';
    // Explicitly load so Firefox parses metadata immediately, making
    // currentTime writable before the user has ever pressed play.
    audio.load();
    audioRef.current = audio;

    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.pause();
      audio.removeEventListener('ended', handleEnded);
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, [audioUrl]);

  const startRaf = (audio: HTMLAudioElement) => {
    const tick = () => {
      setCurrentTime(audio.currentTime);
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
  };

  const stopRaf = () => {
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
  };

  const handlePlayPause = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
      stopRaf();
    } else {
      audio.play().catch(() => {});
      setIsPlaying(true);
      startRaf(audio);
    }
  };

  const seekTo = (audio: HTMLAudioElement, targetTime: number) => {
    // Alias to a local const to satisfy no-param-reassign.
    const el = audio;
    if (el.seekable.length > 0) {
      el.currentTime = targetTime;
      setCurrentTime(targetTime);
    } else {
      // Metadata not yet loaded (Firefox, first scrub before load() resolves).
      // Do NOT call load() again here  Ethat resets currentTime to 0 and
      // restarts the fetch. load() was already called in the useEffect;
      // just wait for the in-flight loadedmetadata event.
      el.addEventListener(
        'loadedmetadata',
        () => {
          el.currentTime = targetTime;
          setCurrentTime(targetTime);
        },
        { once: true }
      );
    }
  };

  const handleScrubClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const audio = audioRef.current;
    if (!audio || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    seekTo(audio, ratio * duration);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    const audio = audioRef.current;
    if (!audio || !duration) return;

    const SEEK_STEP = 5;
    let newTime = currentTime;

    if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') {
      e.preventDefault();
      newTime = Math.max(0, currentTime - SEEK_STEP);
    } else if (e.key === 'ArrowRight' || e.key === 'ArrowUp') {
      e.preventDefault();
      newTime = Math.min(duration, currentTime + SEEK_STEP);
    } else if (e.key === 'Home') {
      e.preventDefault();
      newTime = 0;
    } else if (e.key === 'End') {
      e.preventDefault();
      newTime = duration;
    } else {
      return;
    }

    seekTo(audio, newTime);
  };

  return (
    <Box alignItems="Center" gap="200" className={css.AudioPreviewContainer}>
      <IconButton
        variant="Secondary"
        size="400"
        radii="300"
        onClick={handlePlayPause}
        title={isPlaying ? 'Pause' : 'Play voice message'}
        aria-label={isPlaying ? 'Pause' : 'Play voice message'}
        aria-pressed={isPlaying}
      >
        {isPlaying ? (
          <Pause size={phosphorSizeRem(20)} weight="fill" />
        ) : (
          <Play size={phosphorSizeRem(20)} weight="fill" />
        )}
      </IconButton>

      <Box
        grow="Yes"
        alignItems="Center"
        gap="100"
        onClick={handleScrubClick}
        onKeyDown={handleKeyDown}
        className={css.AudioWaveformContainer}
        tabIndex={0}
        role="slider"
        aria-label="Audio position"
        aria-valuemin={0}
        aria-valuemax={duration}
        aria-valuenow={Math.floor(currentTime)}
        title="Seek"
      >
        {previewBars.map((bar) => {
          const played = progress > 0 && bar.ratio <= progress;
          return (
            <div
              key={bar.id}
              className={`${css.AudioWaveformBar} ${played ? css.AudioWaveformBarPlayed : css.AudioWaveformBarUnplayed}`}
              style={{ height: Math.max(3, Math.round(bar.level * 24)) }}
            />
          );
        })}
      </Box>

      <Text size="T200" className={css.AudioTimeDisplay}>
        {formatAudioTime(isPlaying ? currentTime : duration)}
      </Text>
    </Box>
  );
}

type MediaPreviewProps = {
  fileItem: TUploadItem;
  onSpoiler: (marked: boolean) => void;
  children: ReactNode;
};
function MediaPreview({ fileItem, onSpoiler, children }: MediaPreviewProps) {
  const { originalFile, metadata } = fileItem;
  const fileUrl = useObjectURL(originalFile);

  return fileUrl ? (
    <Box
      style={{
        borderRadius: config.radii.R300,
        overflow: 'hidden',
        backgroundColor: 'black',
        position: 'relative',
      }}
    >
      {children}
      <Box
        justifyContent="End"
        style={{
          position: 'absolute',
          bottom: config.space.S100,
          left: config.space.S100,
          right: config.space.S100,
        }}
      >
        <Chip
          variant={metadata.markedAsSpoiler ? 'Warning' : 'Secondary'}
          fill="Soft"
          radii="Pill"
          aria-pressed={metadata.markedAsSpoiler}
          before={sizedIcon(EyeSlash, '50')}
          onClick={() => onSpoiler(!metadata.markedAsSpoiler)}
        >
          <Text size="B300">Spoiler</Text>
        </Chip>
      </Box>
    </Box>
  ) : null;
}

type UploadCardRendererProps = {
  isEncrypted?: boolean;
  fileItem: TUploadItem;
  setMetadata: (fileItem: TUploadItem, metadata: TUploadMetadata) => void;
  setDesc: (fileItem: TUploadItem, body: string, formatted_body: string) => void;
  onRemove: (file: TUploadContent) => void;
  onComplete?: (upload: UploadSuccess) => void;
  roomId: string;
  hideCaption?: boolean;
};
export function UploadCardRenderer({
  isEncrypted,
  fileItem,
  setMetadata,
  setDesc,
  onRemove,
  onComplete,
  roomId,
  hideCaption,
}: Readonly<UploadCardRendererProps>) {
  const mx = useMatrixClient();
  const mediaConfig = useMediaConfig();
  const allowSize = mediaConfig['m.upload.size'] || Infinity;

  const uploadAtom = roomUploadAtomFamily(fileItem.file);
  const { metadata } = fileItem;
  const { upload, startUpload, cancelUpload } = useBindUploadAtom(mx, uploadAtom, isEncrypted);
  const { file } = upload;
  const fileSizeExceeded = file.size >= allowSize;

  const [isDescribed, setIsDescribed] = useState(false);

  if (upload.status === UploadStatus.Idle && !fileSizeExceeded && !fileItem.encrypting) {
    startUpload();
  }

  const handleSpoiler = (marked: boolean) => {
    setMetadata(fileItem, { ...metadata, markedAsSpoiler: marked });
  };

  const removeUpload = () => {
    cancelUpload();
    onRemove(file);
  };

  useEffect(() => {
    if (upload.status === UploadStatus.Success) {
      onComplete?.(upload);
    }
  }, [upload, onComplete]);

  const linkifyOpts = useMemo<LinkifyOpts>(() => ({ ...LINKIFY_OPTS }), []);

  const spoilerClickHandler = useSpoilerClickHandler();
  const useAuthentication = useMediaAuthentication();
  const settingsLinkBaseUrl = useSettingsLinkBaseUrl();
  const [incomingInlineImagesDefaultHeight] = useSetting(
    settingsAtom,
    'incomingInlineImagesDefaultHeight'
  );
  const [incomingInlineImagesMaxHeight] = useSetting(settingsAtom, 'incomingInlineImagesMaxHeight');
  const htmlReactParserOptions = useMemo<HTMLReactParserOptions>(
    () =>
      getReactCustomHtmlParser(mx, roomId, {
        settingsLinkBaseUrl,
        linkifyOpts,
        useAuthentication,
        handleSpoilerClick: spoilerClickHandler,
        incomingInlineImagesDefaultHeight,
        incomingInlineImagesMaxHeight,
      }),
    [
      linkifyOpts,
      mx,
      roomId,
      settingsLinkBaseUrl,
      spoilerClickHandler,
      useAuthentication,
      incomingInlineImagesDefaultHeight,
      incomingInlineImagesMaxHeight,
    ]
  );
  return (
    <UploadCard
      radii="300"
      compact
      style={{ maxWidth: toRem(400), flexShrink: 0 }}
      before={sizedIcon(getFileTypeIconComponent(file.type))}
      after={
        <>
          {upload.status === UploadStatus.Error && (
            <Chip
              as="button"
              onClick={startUpload}
              aria-label="Retry Upload"
              variant="Critical"
              radii="Pill"
              outlined
            >
              <Text size="B300">Retry</Text>
            </Chip>
          )}
          {!isDescribed && !hideCaption && (
            <IconButton
              onClick={() => {
                setIsDescribed(true);
              }}
              aria-label="Add Upload Description"
              variant="SurfaceVariant"
              radii="Pill"
              size="300"
            >
              {sizedIcon(PencilSimple, '50')}
            </IconButton>
          )}
          {isDescribed && !hideCaption && (
            <TooltipProvider
              delay={400}
              position="Top"
              style={{ textAlign: 'center' }}
              tooltip={
                <Tooltip>
                  <Text size="H5">
                    Don&apos;t forget to save your description before sending the message!
                  </Text>
                </Tooltip>
              }
            >
              {(triggerRef) => <span ref={triggerRef}>{sizedIcon(Info, '50')}</span>}
            </TooltipProvider>
          )}

          <IconButton
            onClick={removeUpload}
            aria-label="Cancel Upload"
            variant="SurfaceVariant"
            radii="Pill"
            size="300"
          >
            {sizedIcon(X, '200')}
          </IconButton>
        </>
      }
      bottom={
        <>
          {isImageMimeType(fileItem.originalFile.type) && (
            <MediaPreview fileItem={fileItem} onSpoiler={handleSpoiler}>
              <PreviewImage fileItem={fileItem} />
            </MediaPreview>
          )}
          {fileItem.originalFile.type.startsWith('video') && (
            <MediaPreview fileItem={fileItem} onSpoiler={handleSpoiler}>
              <PreviewVideo fileItem={fileItem} />
            </MediaPreview>
          )}
          {fileItem.metadata.waveform && <PreviewAudio fileItem={fileItem} />}
          {upload.status === UploadStatus.Idle && !fileSizeExceeded && (
            <UploadCardProgress sentBytes={0} totalBytes={file.size} />
          )}
          {upload.status === UploadStatus.Loading && (
            <UploadCardProgress sentBytes={upload.progress.loaded} totalBytes={file.size} />
          )}
          {upload.status === UploadStatus.Error && (
            <UploadCardError>
              <Text size="T200">{upload.error.message}</Text>
            </UploadCardError>
          )}
          {upload.status === UploadStatus.Idle && fileSizeExceeded && (
            <UploadCardError>
              <Text size="T200">
                The file size exceeds the limit. Maximum allowed size is{' '}
                <b>{bytesToSize(allowSize)}</b>, but the uploaded file is{' '}
                <b>{bytesToSize(file.size)}</b>.
              </Text>
            </UploadCardError>
          )}

          {isDescribed && !hideCaption && (
            <DescriptionEditor
              value={fileItem.formatted_body || fileItem.body}
              onSave={(plainText, htmlContent) => {
                setDesc(fileItem, plainText, htmlContent);
                setIsDescribed(false);
              }}
              onCancel={() => setIsDescribed(false)}
            />
          )}
          {!isDescribed && !hideCaption && fileItem.body && fileItem.body.length > 0 && (
            <Box style={{ padding: config.space.S200, wordBreak: 'break-word' }}>
              <Text size="T200" priority="400" as="div">
                <RenderBody
                  body={fileItem.body}
                  customBody={fileItem.formatted_body}
                  htmlReactParserOptions={htmlReactParserOptions}
                  linkifyOpts={linkifyOpts}
                />
              </Text>
            </Box>
          )}
        </>
      }
    >
      <Text size="H6" truncate style={{ minWidth: 0, flexGrow: 1 }}>
        {file.name}
      </Text>
      {upload.status === UploadStatus.Success &&
        sizedIcon(Check, '100', { style: { color: color.Success.Main } })}
    </UploadCard>
  );
}
